import { forwardRef, useId } from 'react';

/**
 * A single checkbox with its label and optional hint bound via
 * `.etch-choice` (spec §7.1). The whole row is the touch target
 * (min 44×44, see fields.css) — label wraps input + copy so tapping anywhere
 * on the row toggles it.
 *
 * @param {object} props
 * @param {string} [props.id]
 * @param {import('react').ReactNode} props.label
 * @param {import('react').ReactNode} [props.hint]
 * @param {import('react').ReactNode} [props.error] - takes precedence over `hint`,
 *   same aria-invalid + aria-describedby contract as the text fields.
 * @param {string} [props.className]
 */
export const Checkbox = forwardRef(function Checkbox({ id, label, hint, error, className = '', ...rest }, ref) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <label htmlFor={inputId} className={`etch-choice ${className}`.trim()}>
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      <span className="etch-choice__body">
        <span className="etch-choice__label">{label}</span>
        {error ? (
          <span id={errorId} className="etch-field__error" role="alert">
            {error}
          </span>
        ) : hint ? (
          <span id={hintId} className="etch-choice__hint">
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
});

export default Checkbox;
